import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell } from './LoginPage';
import { humanizeError } from '@/lib/errors';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<{ email: string }>();

  const onSubmit = async ({ email }: { email: string }) => {
    setSubmitting(true);
    setError(null);
    try {
      await authApi.forgotPassword(email);
      setSent(true);
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t('auth.resetPassword')}>
      {sent ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-card bg-success-container p-4 text-on-success-container">
            <span className="material-symbols-outlined text-success">check_circle</span>
            <div className="space-y-1">
              <p className="font-semibold">{t('auth.emailSentTitle')}</p>
              <p className="text-sm">{t('auth.resetSent')}</p>
            </div>
          </div>
          <Link to="/login" replace className="inline-block font-semibold text-primary">
            ← {t('auth.signIn')}
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            {t('auth.forgotPasswordHint')}
          </p>
          <Input
            type="email"
            autoComplete="email"
            label={t('auth.email')}
            {...register('email', { required: true })}
            error={errors.email && t('auth.errorEmail')}
          />
          {error && <p className="text-sm text-error">{error}</p>}
          <Button type="submit" size="lg" fullWidth loading={submitting}>
            {t('common.submit')}
          </Button>
          <Link to="/login" replace className="block text-center text-sm text-on-surface-variant">
            ← {t('common.back')}
          </Link>
        </form>
      )}
    </AuthShell>
  );
}
